//#region src/surfaces/recents-log.ts
/** The most entries a log keeps; an append or a merge keeps the newest this many. */
const RECENTS_LOG_CAP = 100;
/** The longest app slug worth keeping. Bounds a parsed log to about 14 KB at the adversarial ceiling. */
const RECENTS_MAX_APP_ID_LEN = 128;
/**
* How far ahead of `now` an entry may sit before the log treats it as clock skew: ten minutes.
* Four guards use it. `appendRecentOpen` evicts future-dated entries from the log it extends.
* The cap ranking evicts future-dated entries before any past-dated one, so a bad-clock block
* never displaces a real open. The default shell's reader hides future-dated entries.
* `mergeRecentsLogs` filters the device side, never the server side, before the union.
*/
const RECENTS_MAX_CLOCK_SKEW_MS = 6e5;
/** The empty log, shared so callers can compare identity. */
const EMPTY_RECENTS_LOG = Object.freeze({ log: Object.freeze([]) });
/** Oldest first; a tie on `t` breaks by app slug, so two devices reduce to one list. */
function byTimeThenId(x, y) {
	return x.t - y.t || (x.a < y.a ? -1 : 1);
}
/**
* Dedupe by `(a, t)`, sort oldest first, and keep at most {@link RECENTS_LOG_CAP} entries. With
* a finite `now`, future-dated entries go first when the cap binds; with no `now` or a non-finite
* one, the newest entries are kept unconditionally.
*/
function normalizeEntries(entries, now) {
	const seen = /* @__PURE__ */ new Set();
	const unique = [];
	for (const e of entries) {
		const k = `${e.t}\n${e.a}`;
		if (seen.has(k)) continue;
		seen.add(k);
		unique.push(e);
	}
	unique.sort(byTimeThenId);
	if (unique.length <= 100) return unique;
	if (now === void 0 || !Number.isFinite(now)) return unique.slice(-100);
	const horizon = now + RECENTS_MAX_CLOCK_SKEW_MS;
	const past = unique.filter((e) => e.t <= horizon);
	const future = unique.filter((e) => e.t > horizon);
	const kept = past.length >= 100 ? past.slice(past.length - 100) : [...past, ...future.slice(future.length - (100 - past.length))];
	kept.sort(byTimeThenId);
	return kept;
}
/**
* Read any value as a log. Accepts the wrapper object `{ log: [...] }` or a bare entry array.
* An entry survives only with a non-empty string `a` of at most {@link RECENTS_MAX_APP_ID_LEN}
* characters after trimming, and a finite number `t`, truncated to an integer. Anything else is
* the empty log. Deduped, oldest first, capped at the newest {@link RECENTS_LOG_CAP}.
*/
function parseRecentsLog(value) {
	const raw = Array.isArray(value) ? value : typeof value === "object" && value !== null && Array.isArray(value.log) ? value.log : null;
	if (raw === null) return EMPTY_RECENTS_LOG;
	const entries = [];
	for (const item of raw) {
		if (typeof item !== "object" || item === null) continue;
		const candidate = item;
		const a = typeof candidate.a === "string" ? candidate.a.trim() : "";
		if (a === "" || a.length > 128) continue;
		if (typeof candidate.t !== "number" || !Number.isFinite(candidate.t)) continue;
		entries.push({
			a,
			t: Math.trunc(candidate.t)
		});
	}
	return { log: normalizeEntries(entries) };
}
/**
* Append one entry for `appId` at `now`. Returns the input log itself, same reference, when
* there is nothing to record: a non-string, blank or oversized `appId`, or a non-finite `now`.
* Otherwise evicts entries more than {@link RECENTS_MAX_CLOCK_SKEW_MS} ahead of `now`, appends
* the trimmed slug, and normalizes under the ranked cap.
*/
function appendRecentOpen(log, appId, now) {
	if (typeof appId !== "string") return log;
	const a = appId.trim();
	if (a === "" || a.length > 128) return log;
	if (!Number.isFinite(now)) return log;
	return { log: normalizeEntries([...log.log.filter((e) => e.t <= now + RECENTS_MAX_CLOCK_SKEW_MS), {
		a,
		t: Math.trunc(now)
	}], now) };
}
/**
* Union the server log with the device log at `now`, then cap. The server side is never
* filtered; the device side drops entries past the skew horizon first, so a block planted in
* device storage never reaches the server. Call it positionally as `(server, device, now)`: a
* swapped call filters the server and admits the device.
*/
function mergeRecentsLogs(server, device, now) {
	const horizon = now + RECENTS_MAX_CLOCK_SKEW_MS;
	return { log: normalizeEntries([...server.log, ...device.log.filter((e) => e.t <= horizon)], now) };
}
/** Value equality, entry by entry. The guard that stops a write-back loop. */
function recentsLogsEqual(a, b) {
	if (a.log.length !== b.log.length) return false;
	for (let i = 0; i < a.log.length; i += 1) {
		const x = a.log[i];
		const y = b.log[i];
		if (x.a !== y.a || x.t !== y.t) return false;
	}
	return true;
}
//#endregion
export { EMPTY_RECENTS_LOG, RECENTS_LOG_CAP, RECENTS_MAX_APP_ID_LEN, RECENTS_MAX_CLOCK_SKEW_MS, appendRecentOpen, mergeRecentsLogs, parseRecentsLog, recentsLogsEqual };

//# sourceMappingURL=recents-log.js.map